import React, { useState } from "react";
import "./FileUpload.css";

const FileUpload = ({ onUploadComplete, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");
      setSuccess("");
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Пожалуйста, выберите файл");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");
    setUploadProgress(0);

    try {
      console.log("Начало загрузки файла...");
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Требуется авторизация");
      }
      console.log("Токен найден");

      // Размер чанка: 2MB (безопасно для API Gateway)
      const CHUNK_SIZE = 2 * 1024 * 1024;
      const fileSize = selectedFile.size;
      const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);
      
      console.log(`Файл размером ${formatFileSize(fileSize)} будет разбит на ${totalChunks} частей`);

      // Шаг 1: Инициализируем загрузку
      const initResponse = await fetch("/api/user/upload-file-init", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          filename: selectedFile.name,
          fileSize: fileSize,
          totalChunks: totalChunks,
        }),
      });

      if (!initResponse.ok) {
        const errorData = await initResponse.json();
        throw new Error(errorData.error || "Не удалось инициализировать загрузку");
      }

      const { uploadId } = await initResponse.json();
      console.log("Загрузка инициализирована, uploadId:", uploadId);

      // Шаг 2: Загружаем чанки
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileSize);
        const chunk = selectedFile.slice(start, end);

        const formData = new FormData();
        formData.append("file", chunk, selectedFile.name);
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", chunkIndex);
        formData.append("totalChunks", totalChunks);

        console.log(`Загрузка чанка ${chunkIndex + 1}/${totalChunks}...`);

        const chunkResponse = await fetch("/api/user/upload-file-chunk", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!chunkResponse.ok) {
          const errorData = await chunkResponse.json();
          throw new Error(errorData.error || `Ошибка загрузки чанка ${chunkIndex + 1}`);
        }

        // Обновляем прогресс
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(progress);
      }

      // Шаг 3: Финализируем загрузку (собираем чанки)
      console.log("Финализация загрузки...");
      const finalizeResponse = await fetch("/api/user/upload-file-finalize", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          uploadId: uploadId,
          filename: selectedFile.name,
        }),
      });

      if (!finalizeResponse.ok) {
        const errorData = await finalizeResponse.json();
        throw new Error(errorData.error || "Ошибка финализации загрузки");
      }

      const result = await finalizeResponse.json();
      console.log("Файл успешно загружен:", result);

      setSuccess(`Файл успешно загружен: ${selectedFile.name}`);
      setSelectedFile(null);
      
      if (onUploadComplete) {
        onUploadComplete(result);
      }
      
      // Автоматически закрываем через 2 секунды после успешной загрузки
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 2000);
      
      setUploading(false);
    } catch (error) {
      console.error("Upload error:", error);
      setError(error.message || "Ошибка при загрузке файла");
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError("");
    setSuccess("");
    setUploadProgress(0);
  };

  return (
    <div className="file-upload-container">
      <h3>Загрузить файл</h3>

      <div className="file-input-wrapper">
        <input
          type="file"
          id="file-input"
          onChange={handleFileSelect}
          disabled={uploading}
          className="file-input"
        />
        <label htmlFor="file-input" className="file-input-label">
          {selectedFile ? (
            <div className="selected-file-info">
              <span className="file-icon">📄</span>
              <div className="file-details">
                <div className="file-name">{selectedFile.name}</div>
                <div className="file-size">{formatFileSize(selectedFile.size)}</div>
              </div>
            </div>
          ) : (
            <div className="file-placeholder">
              <span className="upload-icon">☁️</span>
              <span>Выберите файл для загрузки (формат: .crd)</span>
            </div>
          )}
        </label>
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-text">{uploadProgress}%</div>
        </div>
      )}

      {error && <div className="upload-message error">{error}</div>}
      {success && <div className="upload-message success">{success}</div>}

      <div className="upload-actions">
        {selectedFile && !uploading && !success && (
          <button className="btn-cancel" onClick={handleCancel}>
            Отменить
          </button>
        )}
        <button
          className="btn-upload"
          onClick={handleUpload}
          disabled={!selectedFile || uploading || success}
        >
          {uploading ? "Загружается..." : "Загрузить"}
        </button>
      </div>
    </div>
  );
};

export default FileUpload;
